import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Chip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Work as JobIcon,
  Handyman as ServiceIcon,
  Payment as PaymentIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import DatabaseService from '../../services/DatabaseService';
import socketService from '../../services/SocketService';
import { useCurrency } from '../../context/CurrencyContext';

// Notification Context
const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const { currentUser } = useAuth();
  const { formatCurrency } = useCurrency();

  // Add notification
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    
    // Show snackbar for real-time notifications
    showSnackbar(notification.message, notification.type || 'info');
    
    // Save to database
    if (currentUser) {
      DatabaseService.addNotification(currentUser.uid, newNotification);
    }
  }, [currentUser]);

  // Mark notification as read
  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, read: true }
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    if (currentUser) {
      DatabaseService.markNotificationAsRead(currentUser.uid, notificationId);
    }
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    setUnreadCount(0);
    
    if (currentUser) {
      DatabaseService.markAllNotificationsAsRead(currentUser.uid);
    }
  };

  // Show snackbar
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Hide snackbar
  const hideSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Load notifications on mount and setup Socket.IO listeners
  useEffect(() => {
    if (currentUser) {
      const loadNotifications = async () => {
        try {
          const userNotifications = await DatabaseService.getUserNotifications(currentUser.uid);
          setNotifications(userNotifications);
          setUnreadCount(userNotifications.filter(n => !n.read).length);
        } catch (error) {
          console.error('Error loading notifications:', error);
          // Set empty array as fallback to prevent crashes
          setNotifications([]);
          setUnreadCount(0);
        }
      };
      
      loadNotifications();
      
      // Setup Socket.IO listeners for real-time notifications
      const unsubscribeNotification = socketService.subscribe('notification', (data) => {
        addNotification(data);
      });
      
      const unsubscribeJobApplication = socketService.subscribe('job_application', (data) => {
        addNotification({
          type: 'job_application',
          title: 'New Job Application',
          message: `${data.applicantName} applied for ${data.jobTitle}`,
          data: data
        });
      });
      
      const unsubscribeServiceBooking = socketService.subscribe('service_booking', (data) => {
        addNotification({
          type: 'service_booking',
          title: 'New Service Booking',
          message: `${data.clientName} booked ${data.serviceName}`,
          data: data
        });
      });
      
      const unsubscribeMessage = socketService.subscribe('message', (data) => {
        addNotification({
          type: 'message',
          title: `New message from ${data.senderName}`,
          message: data.content,
          data: data
        });
      });
      
      const unsubscribeApprovalUpdate = socketService.subscribe('approval_update', (data) => {
        addNotification({
          type: 'approval',
          title: `${data.itemType} ${data.status}`,
          message: data.message,
          data: data
        });
      });
      
      const unsubscribePaymentUpdate = socketService.subscribe('payment_update', (data) => {
        addNotification({
          type: 'payment',
          title: 'Payment Update',
          message: `Payment ${data.status}: ${formatCurrency(data.amount)}`,
          data: data
        });
      });
      
      // Cleanup function
      return () => {
        unsubscribeNotification();
        unsubscribeJobApplication();
        unsubscribeServiceBooking();
        unsubscribeMessage();
        unsubscribeApprovalUpdate();
        unsubscribePaymentUpdate();
      };
    }
  }, [currentUser, addNotification, formatCurrency]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    showSnackbar,
    hideSnackbar
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

// Notification Bell Component
export const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'job_application':
        return <JobIcon color="primary" />;
      case 'service_booking':
        return <ServiceIcon color="primary" />;
      case 'approval':
        return <ApprovedIcon color="success" />;
      case 'rejection':
        return <RejectedIcon color="error" />;
      case 'payment':
        return <PaymentIcon color="warning" />;
      case 'message':
        return <MessageIcon color="info" />;
      default:
        return <NotificationsIcon color="action" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'approval':
        return 'success';
      case 'rejection':
        return 'error';
      case 'payment':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notifTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ ml: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 400, maxHeight: 500 }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllAsRead}>
              Mark all as read
            </Button>
          )}
        </Box>
        <Divider />
        
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 350, overflow: 'auto' }}>
            {notifications.slice(0, 10).map((notification) => (
              <ListItem
                key={notification.id}
                onClick={() => markAsRead(notification.id)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: notification.read ? 'transparent' : 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <ListItemIcon>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {notification.title}
                      </Typography>
                      {!notification.read && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main'
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {notification.message}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(notification.timestamp)}
                        </Typography>
                        {notification.category && (
                          <Chip
                            label={notification.category}
                            size="small"
                            color={getNotificationColor(notification.type)}
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
        
        {notifications.length > 10 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" fullWidth>
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

// Notification Types for easy reference
export const NOTIFICATION_TYPES = {
  JOB_APPLICATION: 'job_application',
  SERVICE_BOOKING: 'service_booking',
  APPROVAL: 'approval',
  REJECTION: 'rejection',
  PAYMENT: 'payment',
  MESSAGE: 'message',
  SYSTEM: 'system'
};

// Helper function to create notifications
export const createNotification = (type, title, message, category = null, data = null) => ({
  type,
  title,
  message,
  category,
  data
});

// Main NotificationSystem component that combines Provider and Bell
const NotificationSystem = ({ children }) => {
  return (
    <NotificationProvider>
      {children}
    </NotificationProvider>
  );
};

export default NotificationSystem;